import { nGetCsrf } from "./Csrf.js";
import { nRemoveAuth } from "./ReqShare.js";

const MAX_RETRY = 1;

if (typeof window.isRefreshing === "undefined") {
  window.isRefreshing = false;
  window.refreshQueue = [];
}

// 不需要觸發 refresh 的 GET API
const noRefreshList = [
  /^\/explore\/top-styles$/,
  /^\/companies$/,
  /^\/styles$/,
  /^\/comments$/,
  /^\/stats$/,
  /^\/auth\/csrf-token$/,
  /^\/explore\/styles$/,
  // /^\/explore\/projects\/[^/]+$/,
  // /^\/explore\/companies\/[^/]+$/,
  // /^\/explore\/companies\/[^/]+\/[^/]+$/,
  /^\/options$/,
  /^\/auth\/login$/,
  /^\/auth\/email-verification$/,
  /^\/auth\/check-email$/, // 註冊頁：檢查 Email 是否已被註冊
];

// 特例 POST，不觸發 refresh
const noRefreshPostList = [
  /^\/auth\/otp$/,
  /^\/auth\/login$/,
  /^\/auth\/logout$/,
  /^\/auth\/forgot-password$/,
  /^\/auth\/reset-password$/,
  /^\/auth\/refresh$/,
  /^\/users$/, // 註冊頁：用戶註冊
];

/**
 * 判斷是否需要觸發 refresh
 */

function shouldTriggerRefresh(url, method) {
  const path = new URL(url, window.location.origin).pathname;

  if (method === "GET") {
    return !noRefreshList.some(pattern => pattern.test(path));
  } else if (method === "POST") {
    return !noRefreshPostList.some(pattern => pattern.test(path));
  } else {
    // PUT / PATCH / DELETE 一律觸發
    return true;
  }
}
/**
 * 判斷是否要帶 credentials
 */
function shouldIncludeCredentials(url, method) {
  const path = new URL(url, window.location.origin).pathname;

  if (method === "GET") return !noRefreshList.some(pattern => pattern.test(path));
  return true; // POST / PUT / DELETE 一律帶 credentials
}

async function xmFETCH({
  method = "GET",
  url,
  body,
  retryCount = 0,
  timeout = 30000 // 默認 30 秒
}) {
  const Language = localStorage.getItem("language") || "zh-TW";
  document.body.style.pointerEvents = "none";

  try {
    // 只在 production 環境加上 /api 前綴（開發環境透過 package.json proxy 轉發）
    const apiUrl = process.env.NODE_ENV === 'production' && !url.startsWith('/api/') ? `/api${url}` : url;

    const headers = { "Accept-Language": Language };

    if (!(body instanceof FormData) && method !== "GET") {
      headers["Content-Type"] = "application/json";
    }
    if (method !== "GET" && window.xmRET_CSF?.token) {
      headers["x-csrf-token"] = window.xmRET_CSF.token;
    }

    const credentials = shouldIncludeCredentials(url, method) ? "include" : "same-origin";

    // 創建 AbortController 用於 timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(apiUrl, {
        method,
        headers,
        body: body && method !== "GET" ? body : undefined,
        credentials,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      return await handleResponse(response, method, url, apiUrl, body, retryCount);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw { status: 408, message: '請求超時，請檢查網路連線或稍後再試' };
      }
      throw fetchError;
    }
  } catch (err) {
    throw err;
  } finally {
    document.body.style.pointerEvents = "auto";
  }
}

// 將 response 處理邏輯抽取為獨立函數
async function handleResponse(response, method, url, apiUrl, body, retryCount) {

    const rawText = await response.text();

    // ---------- 成功處理 ----------
    if (response.ok) {
      if (!rawText) return { success: true, status: response.status };
      try { return JSON.parse(rawText); } 
      catch { return rawText; }
    }

    // ---------- 401 處理 ----------
    if (response.status === 401) {
      // /auth/refresh 本身發生 401 或 403 → session 過期
      // 檢查原始 url 和轉換後的 apiUrl
      const isRefreshEndpoint = url === "/auth/refresh" || apiUrl === "/api/auth/refresh";
      if (isRefreshEndpoint) {
        nRemoveAuth();
        throw { status: 401, message: "SESSION_EXPIRED" };
      }

      // 如果不該自動 refresh（GET 黑名單或 POST 特例）
      if (!shouldTriggerRefresh(url, method)) {
        // 直接丟錯給前端
        throw makeErrObj(response.status, rawText);
      }
      // 檢查是否需要 refresh
      if (!shouldTriggerRefresh(url, method)) {
        const path = url.split("?")[0];
        return !noRefreshList.some(pattern => pattern.test(path));
      }
      if (retryCount >= MAX_RETRY) {
        nRemoveAuth();
        throw makeErrObj(response.status, rawText);
      }

      // 將當前請求排入 refresh queue
      return new Promise((resolve, reject) => {
        window.refreshQueue.push({
          method,
          url,
          body,
          retryCount: retryCount + 1,
          resolve,
          reject
        });

        if (window.isRefreshing) return;

        window.isRefreshing = true;

        (async () => {
          try {
            const newCSF = await nGetCsrf();
            // 根據環境決定是否加 /api 前綴
            const refreshUrl = process.env.NODE_ENV === 'production'
              ? "/api/auth/refresh"
              : "/auth/refresh";
            const refreshResp = await fetch(refreshUrl, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "x-csrf-token": newCSF?.token
              }
            });

            if (refreshResp.status === 401 || refreshResp.status === 403) {
              nRemoveAuth();
              const unifiedError = { status: refreshResp.status, message: "SESSION_EXPIRED" };
              const queued = [...window.refreshQueue];
              window.refreshQueue = [];
              queued.forEach(req => req.reject(unifiedError));
              reject(unifiedError);
              return;
            }

            if (!refreshResp.ok) {
              nRemoveAuth();
              // 刷新失敗，拋出統一錯誤
              const unifiedError = { status: refreshResp.status, message: "SESSION_EXPIRED" };
              reject(unifiedError);
              return;
            } else {
              // 刷新成功，標記已登入並通知 UserContext 更新使用者資料
              localStorage.setItem('auth', 1);
              window.dispatchEvent(new Event('auth-update'));
              await nGetCsrf();
            }

            // 重試所有排隊的請求
            const queued = [...window.refreshQueue];
            window.refreshQueue = [];
            queued.forEach(req => {
              xmFETCH({ ...req }).then(req.resolve).catch(req.reject);
            });
          } catch (err) {
            nRemoveAuth();
            const queued = [...window.refreshQueue];
            window.refreshQueue = [];
            const unifiedError = { status: 440, message: "SESSION_EXPIRED" };
            queued.forEach(req => req.reject(unifiedError));

            if (!["/", "/login", "/register"].includes(window.location.pathname)) {
              window.location.replace("/");
            }
          } finally {
            window.isRefreshing = false;
          }
        })();
      });
    }

    // ---------- 403 ----------
    const isRefreshEndpoint403 = url === "/auth/refresh" || apiUrl === "/api/auth/refresh";
    if (response.status === 403 && !isRefreshEndpoint403) {
      throw makeErrObj(response.status, rawText);
    }

    if (response.status === 404) {
      return { success: false, status: response.status };
    }

    if (response.status.status >= 500 && response.status < 600) {
      console.log(JSON.stringify(response));
      throw { success: false, status: response.status };
    }

    // ---------- 其他錯誤 ----------
    throw makeErrObj(response.status, rawText);
}

// 生成統一錯誤物件
function makeErrObj(status, rawText) {
  let msg;
  try {
    const parsed = JSON.parse(rawText);

    // 優先使用頂層 message (符合 {"message":"...", "error":"..."} 格式)
    if (parsed?.message) {
      msg = parsed.message;
    }
    // 處理後端統一錯誤格式: { error: { code, message, details } }
    else if (parsed?.error && typeof parsed.error === 'object') {
      // 如果有 details（驗證錯誤），提取第一個錯誤的 msg
      if (parsed.error.details && Array.isArray(parsed.error.details) && parsed.error.details.length > 0) {
        msg = parsed.error.details[0].msg || parsed.error.message;
      } else {
        msg = parsed.error.message;
      }
    } else {
      // 向後兼容舊格式
      msg = parsed?.message || rawText;
    }
  } catch {
    msg = rawText;
  }
  return { status, message: msg };
}

// ---------------- 封裝不同 HTTP method ----------------
export const xmREQ_Work = (url, body) => xmFETCH({ method: "GET", url, body });
export const xmREP_Work = (url, body) => xmFETCH({ method: "POST", url, body });
export const xmRET_Work = (url, body) => xmFETCH({ method: "PUT", url, body });
export const xmREH_Work = (url, body) => xmFETCH({ method: "PATCH", url, body });
export const xmRED_Work = (url, body) => xmFETCH({ method: "DELETE", url, body });
// 圖片上傳使用 120 秒 timeout（並發上傳多張圖片需要更長時間）
export const xmREP_IMG_Upload = (url, body) => xmFETCH({ method: "POST", url, body, timeout: 120000 });
